import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import Button from 'components/Button/Button';
import RevenueFilter from 'components/RevenueFilter/RevenueFilter';
import SidePanel from 'components/SidePanel/SidePanel';
import Toggle from 'components/Toggle/Toggle';
import { NAME_FILTERS } from 'constants/dataSets';
import { FOLLOW_CONNECTIONS_TOGGLE_NAME } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { getFilterIdentifier, isCountryCodeFilter, parseFilterSelectionKey } from 'utils/valueChainHelpers';

import FilterListDefault from './components/FilterListDefault';
import FilterListOrgStructure from './components/FilterListOrgStructure';

import type ICheckboxTreeItem from 'components/CheckboxTree/interfaces/ICheckboxTreeItem';
import type IFilterData from 'pages/CompanyAdminPages/CompanyMap/components/ValueChain/interfaces/IFilterData';
import type IOrgStructureItem from 'store/slices/dataSets/interfaces/IOrgStructureItem';
import type IFilters from './interfaces/IFilters';
import type IFiltersPanel from './interfaces/IFiltersPanel';

import styles from './sass/FiltersPanel.module.scss';

function FiltersPanel({
  open, onClose, filtersData, onApplyFilters, selectedCurrentFilters, setSelectedCurrentFilters,
  followConnections, onToggleFollowConnections, orgStructureData,
}: IFiltersPanel) {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string>('');
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [filters, setFilters] = useState<IFilters>({});
  const [checkedFilters, setCheckedFilters] = useState<IFilterData[]>([]);
  const [previousFilters, setPreviousFilters] = useState<IFilterData[]>([]);
  // Tri-state source of truth for the org tree: null = inactive, [] = active-empty, [...] = partial
  const [orgStructureSelection, setOrgStructureSelection] = useState<string[] | null>(null);
  // Active-empty = org filter engaged but everything unchecked (cannot be applied)
  const isOrgActiveEmpty = selectedFilter === NAME_FILTERS.ORG_STRUCTURE
    && orgStructureSelection?.length === 0;

  const orgStructureLeaves = useMemo<IOrgStructureItem[]>(() => {
    const collectLeaves = (items: IOrgStructureItem[]): IOrgStructureItem[] => items
      .filter((item) => !item.archived)
      .flatMap((item) => {
        const childLeaves = collectLeaves(item.children);
        return childLeaves.length > 0 ? childLeaves : [item];
      });

    return collectLeaves(orgStructureData || []);
  }, [orgStructureData]);

  const allOrgStructureLeafIds = useMemo(
    () => orgStructureLeaves.map((leaf) => leaf.orgPath),
    [orgStructureLeaves],
  );

  const getOrgSelectionFromFilters = useCallback((filterList: IFilterData[]): string[] | null => {
    const orgFilters = filterList.filter((filter) => filter.type === NAME_FILTERS.ORG_STRUCTURE);

    if (orgFilters.length === 0) {
      return null;
    }

    return orgStructureLeaves
      .filter((leaf) => orgFilters.some((filter) => getFilterIdentifier(filter) === getFilterIdentifier(leaf)))
      .map((leaf) => leaf.orgPath);
  }, [orgStructureLeaves]);

  // Convert the tri-state selection into committable org filter entries (empty = no org filter)
  const buildOrgStructureFilters = (selection: string[] | null): IFilterData[] => {
    if (selection === null || selection.length === orgStructureLeaves.length) {
      return [];
    }

    return orgStructureLeaves
      .filter((leaf) => selection.includes(leaf.orgPath))
      .map((leaf) => ({
        name: leaf.name,
        uniqueIdentifier: leaf.uniqueIdentifier,
        min: undefined,
        max: undefined,
        type: NAME_FILTERS.ORG_STRUCTURE,
        category: NAME_FILTERS.ORG_STRUCTURE,
      }));
  };

  useEffect(() => {
    if (!filtersData || filtersData.length === 0) {
      setFilters({});
      return;
    }

    const groupedFilters = filtersData.reduce<IFilters>((acc, item) => {
      const { type } = item;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(item);
      return acc;
    }, {});

    setFilters(groupedFilters);
  }, [filtersData]);

  useEffect(() => {
    // Keep only non-org filters in the working copy; org is owned by orgStructureSelection
    setCheckedFilters(selectedCurrentFilters.filter((filter) => filter.type !== NAME_FILTERS.ORG_STRUCTURE));
    // Seed the org tree from the committed filter (null = inactive, [...] = partial)
    setOrgStructureSelection(getOrgSelectionFromFilters(selectedCurrentFilters));
  }, [selectedCurrentFilters, getOrgSelectionFromFilters]);

  const handleToggleFollowConnections = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggleFollowConnections(event.target.checked);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedFilterSelectionKey = parseFilterSelectionKey(event.target.name);

    if (!parsedFilterSelectionKey) {
      return;
    }

    const { uniqueIdentifier, type } = parsedFilterSelectionKey;

    const indexCurrentSelected = checkedFilters.findIndex((filter) => getFilterIdentifier(filter) === uniqueIdentifier
    && filter.type === type);

    let updatedFilters: IFilterData[];

    if (indexCurrentSelected !== -1) {
      updatedFilters = checkedFilters.filter((filter) => !(getFilterIdentifier(filter) === uniqueIdentifier
      && filter.type === type));
    } else {
      const filterToAdd = filtersData?.find((filter) => getFilterIdentifier(filter) === uniqueIdentifier
      && filter.type === type);

      if (filterToAdd) {
        updatedFilters = [...checkedFilters, filterToAdd];
      } else {
        return;
      }
    }

    setCheckedFilters(updatedFilters);
    setHasChanges(true);
  };

  // Single commit choke-point: merges org back in from the selection so unrelated
  // commits (revenue, country, reset) never drop the org filter
  const handleCommitFilters = (nonOrgFilters: IFilterData[], orgSelection: string[] | null = orgStructureSelection) => {
    const withoutOrg = nonOrgFilters.filter((filter) => filter.type !== NAME_FILTERS.ORG_STRUCTURE);
    const committedFilters = [...withoutOrg, ...buildOrgStructureFilters(orgSelection)];
    setCheckedFilters(withoutOrg);
    setPreviousFilters(withoutOrg);
    setSelectedCurrentFilters(committedFilters);
    onApplyFilters(committedFilters);
    setHasChanges(false);
  };

  const onResetAllCurrent = () => {
    const updatedFilters = checkedFilters.filter((filter) => filter.type !== selectedFilter
    && filter.category !== selectedFilter);
    // Resetting the org sub-panel clears its selection (inactive)
    const orgSelection = selectedFilter === NAME_FILTERS.ORG_STRUCTURE ? null : orgStructureSelection;
    handleCommitFilters(updatedFilters, orgSelection);
  };

  const onResetAll = () => {
    handleCommitFilters([], null);
  };

  const onBackStepFilter = () => {
    // Back while active-empty means "reset to inactive": clear the org filter from the map
    if (isOrgActiveEmpty) {
      handleCommitFilters(checkedFilters, null);
      setSelectedFilter(undefined);
      return;
    }
    setCheckedFilters(previousFilters);
    // Revert the org tree to whatever is currently committed
    setOrgStructureSelection(getOrgSelectionFromFilters(selectedCurrentFilters));
    setSelectedFilter(undefined);
    setHasChanges(false);
  };

  const onSelectedFilterCategory = (filter: string) => {
    setPreviousFilters([...checkedFilters]);
    setSelectedFilter(filter);
    setSearch('');
    setHasChanges(false);
  };

  const onApplyFilter = () => {
    handleCommitFilters(checkedFilters);
    setSelectedFilter(undefined);
  };

  const onApplyRevenueFilter = (minValue: number, maxValue: number) => {
    const revenueFilter = filtersData?.find((filter) => filter.type === NAME_FILTERS.REVENUE);

    if (revenueFilter) {
      const updatedRevenueFilter: IFilterData = {
        ...revenueFilter,
        min: String(minValue),
        max: String(maxValue),
      };

      const filteredWithoutRevenue = checkedFilters.filter((filter) => filter.type !== NAME_FILTERS.REVENUE);
      const updatedFilters = [...filteredWithoutRevenue, updatedRevenueFilter];

      handleCommitFilters(updatedFilters);
    }
  };

  const onResetRevenueFilter = () => {
    const filteredWithoutRevenue = checkedFilters.filter((filter) => filter.type !== NAME_FILTERS.REVENUE);
    handleCommitFilters(filteredWithoutRevenue);
  };

  const orgStructureTreeItems = useMemo<ICheckboxTreeItem[]>(() => {
    const mapTreeItems = (items: IOrgStructureItem[]): ICheckboxTreeItem[] => items
      .filter((item) => !item.archived)
      .map((item) => ({
        id: item.orgPath,
        label: item.name,
        children: mapTreeItems(item.children),
      }));

    return mapTreeItems(orgStructureData || []);
  }, [orgStructureData]);

  const displayedOrgStructureLeafIds = orgStructureSelection ?? allOrgStructureLeafIds;
  const orgStructureFilterCount = orgStructureSelection?.length ?? 0;

  const handleChangeOrgStructureSelection = (selectedLeafIds: string[]) => {
    const selectedLeaves = orgStructureLeaves.filter((leaf) => selectedLeafIds.includes(leaf.orgPath));
    // All checked collapses to inactive; anything else (including empty = active-empty) is explicit
    const nextSelection = selectedLeaves.length === orgStructureLeaves.length
      ? null
      : selectedLeaves.map((leaf) => leaf.orgPath);

    setOrgStructureSelection(nextSelection);
    setHasChanges(true);
  };

  const searchFilters = (filtersArray: IFilterData[], text: string) => {
    if (text) {
      const lowerText = text.toLowerCase();
      return filtersArray.filter((item) => {
        const nameMatch = item?.name.toLowerCase().includes(lowerText);
        const searchValueMatch = item.searchValue?.toLowerCase().includes(lowerText) || false;

        if (selectedFilter === NAME_FILTERS.PROFIT_CENTER || isCountryCodeFilter(selectedFilter)) {
          const identifierMatch = item?.uniqueIdentifier?.toLowerCase().includes(lowerText);
          return nameMatch || identifierMatch || searchValueMatch;
        }

        return nameMatch || searchValueMatch;
      });
    }
    return filtersArray;
  };

  return (
    <SidePanel open={open} className={styles.container}>
      {!selectedFilter && (
        <div className={styles.content}>
          <div className={styles.headerFilters}>
            <span className={styles.headerText}>{t('common.filter')}</span>
            <Button
              title={t('common.resetAll')}
              labelKey="common.resetAll"
              variant={BUTTON_VARIANT.ghost}
              onClick={onResetAll}
            />
          </div>
          <div className={styles.connectionsToggle}>
            <span className={styles.connectionsToggleLabel}>{t('common.seeConnectedItems')}</span>
            <Toggle
              name={FOLLOW_CONNECTIONS_TOGGLE_NAME}
              value={FOLLOW_CONNECTIONS_TOGGLE_NAME}
              checked={followConnections}
              onChange={handleToggleFollowConnections}
            />
          </div>
          <div className={styles.filters}>
            {orgStructureTreeItems.length > 0 ? (
              <div className={styles.buttonWrapper}>
                <Button
                  title={t('common.organizationalStructure')}
                  variant={BUTTON_VARIANT.ghost}
                  onClick={() => onSelectedFilterCategory(NAME_FILTERS.ORG_STRUCTURE)}
                  endIcon={<ChevronIcon />}
                  className={styles.filterButton}
                />
                {orgStructureFilterCount > 0 ? (
                  <div className={styles.circleCounter}>
                    {orgStructureFilterCount}
                  </div>
                ) : null}
              </div>
            ) : null}
            {filters ? Object.keys(filters).map((filterItem, index) => (
              filters[filterItem] && filters[filterItem].length > 0 ? (
                <div key={[filterItem, index].join('_')} className={styles.buttonWrapper}>
                  {filterItem !== NAME_FILTERS.REVENUE && (
                    <Button
                      title={t(`common.${filterItem}`)}
                      variant={BUTTON_VARIANT.ghost}
                      onClick={() => onSelectedFilterCategory(filterItem)}
                      endIcon={<ChevronIcon />}
                      className={styles.filterButton}
                    />
                  )}
                  {filterItem === NAME_FILTERS.REVENUE && filters[filterItem]?.[0] ? (
                    <RevenueFilter
                      revenueData={filters[filterItem][0]}
                      selectedCurrentFilters={checkedFilters}
                      onApplyRevenueFilter={onApplyRevenueFilter}
                      onResetRevenueFilter={onResetRevenueFilter}
                    />
                  ) : null}
                  {checkedFilters.filter((filter) => filter.type === filterItem).length > 0
                   && filterItem !== NAME_FILTERS.REVENUE
                    ? (
                      <div className={styles.circleCounter}>
                        {checkedFilters.filter((filter) => filter.type === filterItem).length}
                      </div>
                    ) : null}
                </div>
              ) : null
            )) : null}
          </div>
        </div>
      )}

      {selectedFilter === NAME_FILTERS.ORG_STRUCTURE ? (
        <FilterListOrgStructure
          items={orgStructureTreeItems}
          selectedLeafIds={displayedOrgStructureLeafIds}
          onChangeSelectedLeafIds={handleChangeOrgStructureSelection}
          onBackStepFilter={onBackStepFilter}
          onResetAllCurrent={onResetAllCurrent}
        />
      ) : null}

      {selectedFilter && selectedFilter !== NAME_FILTERS.REVENUE
        && selectedFilter !== NAME_FILTERS.ORG_STRUCTURE ? (
          <FilterListDefault
            selectedFilter={selectedFilter}
            onBackStepFilter={onBackStepFilter}
            onResetAllCurrent={onResetAllCurrent}
            search={search}
            setSearch={setSearch}
            filtersData={filters[selectedFilter]}
            onChange={onChange}
            searchFilters={searchFilters}
            selectedCurrentFilters={checkedFilters}
          />
        ) : null }

      <div className={styles.buttons}>
        {!selectedFilter ? (
          <Button
            title={t('common.close')}
            labelKey="common.close"
            variant={BUTTON_VARIANT.cancel}
            onClick={onClose}
          />
        ) : (
          <Button
            title={t('common.showSelected')}
            labelKey="common.showSelected"
            variant={BUTTON_VARIANT.primary}
            onClick={onApplyFilter}
            disabled={!hasChanges || isOrgActiveEmpty}
          />
        )}
      </div>
    </SidePanel>
  );
}

export default FiltersPanel;
